<template>
  <div class="flex gap-4 border-t border-gray-300 py-4 max-md:flex-col md:items-center md:justify-between md:gap-8">
    <div>
      <h3
        :id="`webinar-title-${webinar.id}`"
        class="body-md font-semibold"
        v-text="webinar.title"
      />

      <dl class="body-sm flex flex-wrap items-center gap-x-2">
        <div class="flex items-center gap-x-1">
          <dt v-text="`Speaker: `" />
          <dd v-text="webinar.speaker" />
        </div>
        &bull;
        <div class="flex items-center gap-x-1">
          <dt v-text="`CE Credits: `" />
          <dd v-text="webinar.ceCredits" />
        </div>
        <!-- TODO: Add completed date for completed webinars-->
        <template v-if="status === 'completed'">
          &bull;
          <dd v-text="`Completed 07/05/2024`" />
        </template>
      </dl>

      <a
        v-if="status !== 'incomplete' && webinar.status === 'live'"
        :href="webinar.url"
        :id="`view-details-link-${webinar.id}`"
        class="link body-md font-semibold"
        :aria-labelledby="`view-details-link-${webinar.id} webinar-title-${webinar.id}`"
        v-text="`View Details`"
      />
    </div>

    <div class="shrink-0 flex gap-2 md:items-center">
      <template v-if="status === 'incomplete'">
        <button
          type="button"
          :id="`continue-registration-btn-${webinar.id}`"
          class="btn btn--primary flex-1"
          @click="continueRegistration"
          v-text="`Continue`"
        />

        <button
          type="button"
          class="btn btn--outline flex-1"
          @click="$emit('confirmDelete', registration.registration_id)"
          v-text="`Delete`"
        />
      </template>

      <button
        v-if="status === 'current'"
        type="button"
        :id="`access-webinar-btn-${webinar.id}`"
        class="btn btn--primary flex-1"
        :aria-labelledby="`access-webinar-btn-${webinar.id} webinar-title-${webinar.id}`"
        @click="accessWebinar"
        v-text="`Access Webinar`"
      />

      <!-- TODO: Connect view certificate button -->
      <button
        v-if="status === 'completed'"
        type="button"
        :id="`view-certificate-btn-${webinar.id}`"
        class="btn btn--outline flex-1"
        :aria-labelledby="`view-certificate-btn-${webinar.id} webinar-title-${webinar.id}`"
        v-text="`View Certificate`"
      />
    </div>
  </div>

  <RefundDisclaimer
    :registration-id="registration.registration_id"
    :show-access-modal="showAccessModal"
    :customer-id="user?.id ?? 0"
    :country-id="registration.personal.country ?? 'US'"
    @close-access-modal="showAccessModal = false"
  />
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { Course } from '@js/api/types/course/Course'
import { CustomerRegistration } from '@js/api/types/customer/CustomerRegistration'
import { checkoutLinks } from '@js/utils/links'
import { log } from '@js/utils/logger'
import { env } from '@js/utils/env'

import useAuth from '@js/composables/useAuth'
import useCart from '@js/composables/useCart'

import RefundDisclaimer from '@js/components/account/dashboard/RefundDisclaimer.vue'

defineOptions({
  name: 'WebinarCard',
})

const props = defineProps<{
  registration: CustomerRegistration,
  webinar: Course,
  status: 'incomplete' | 'current' | 'completed'
}>()

defineEmits<{
  confirmDelete: [id: number]
  continueRegistration: [id: number]
}>()

const { setLastRegistration, getAuthUser } = useAuth()
const { addItemToCart } = useCart()

const showAccessModal = ref(false)
const isLoading = ref(false)
const user = computed(() => getAuthUser())
const attestationAccepted = ref(props.registration.attestation || false)

const continueRegistration = async () => {
  isLoading.value = true

  const registration = await addItemToCart(
    props.registration.item.sku,
    props.registration.item.course_option,
    props.registration.craft_entry_id,
    props.registration,
  )

  if (registration) {
    setLastRegistration(props.registration.registration_id)

    window.location.assign(checkoutLinks.CHECKOUT)
  } else {
    log({
      message: 'Webinar Registration could not be continued',
      file: 'src/wcei/js/components/account/dashboard/WebinarCard.vue',
    })
  }

  isLoading.value = false
}

const accessWebinar = () => {
  if (!props.registration.attestation) {
    showAccessModal.value = true
  }

  // If the user has NOT accepted the refund disclaimer, show the modal before proceeding
  if (!attestationAccepted.value) {
    showAccessModal.value = true
  } else {
    showAccessModal.value = false 

    try {
      if (!user.value?.rlpDashboardToken) {
        throw new Error('No RLP token available')
      }

      const ssoUrl = `${env.rlp.SSO_URL}${encodeURIComponent(user?.value.rlpDashboardToken)}`

      window.open(ssoUrl, '_blank', 'noopener,noreferrer')
    }
    catch (error) {
      log({
        message: `Error accessing course: ${error}`,
        file: 'src/wcei/js/components/account/dashboard/CourseCard.vue',
      })
    }
  }
}
</script>
